/**
 * /projects/:projectId/settings — project settings page.
 *
 * Tabs (in-component state, not nested routes):
 *   - General  — name / description / code (Edit project) + Danger Zone
 *                (Archive / Unarchive). Phase 3.5 Wave 2 Batch B.
 *   - Labels   — full CRUD via FE-2's `useProjectLabels` hooks.
 *   - Statuses — column management (CRUD + reorder + recolor +
 *                allows-timer toggle). Wave 2 Batch B.
 *   - Members  — roster + invite / role / remove.
 *
 * Auth gate mirrors `routes/board/$projectId.tsx`: no access token →
 * bounce to /login before kicking off the API roundtrip.
 *
 * Access gate: the WHOLE page is project owner/admin only, matching the
 * BE, which gates project update / archive / statuses / members on
 * `assertProjectAdmin`. Three of the four tabs are therefore writable by
 * nobody else, and the fourth (Labels) is not read-only either — label
 * CRUD has no BE gate at all — so there is no honest read-only subset to
 * leave open, and a non-admin gets one clear refusal instead of four
 * panels that 403 on contact. Labels stay creatable where they are
 * actually used: `<LabelPicker>` on a card creates them inline.
 *
 * A non-admin who follows a link here sees an explanation, not a
 * redirect: bouncing them would look like the link was broken.
 *
 * Project lookup: we fetch with `?includeArchived=true` so an archived
 * project is still resolvable on this page (the user needs the
 * Unarchive control). The home page calls without that flag so
 * archived projects vanish from the picker.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError, apiClient } from '@/api/client'
import type { Project } from '@/api/types-bridge'
import { getAccessToken } from '@/auth/cookie-helpers'
import { Button } from '@/components/Button'
import { Modal } from '@/components/Modal'
import { TabBar, TabButton } from '@/components/TabButton'
import { useToast } from '@/components/ToastProvider'
import { useBoard } from '@/features/board/use-board'
import { LabelsSettingsPanel } from '@/features/labels/components/LabelsSettingsPanel'
import { MembersSettingsPanel } from '@/features/labels/components/MembersSettingsPanel'
import { ProjectEditModal } from '@/features/projects/components/ProjectEditModal'
import { StatusesSettingsPanel } from '@/features/projects/components/StatusesSettingsPanel'
import { useIsProjectAdmin } from '@/features/projects/project-admin'
import { useArchiveProject, useUnarchiveProject } from '@/features/projects/use-archive-project'
import { useUpdateProject } from '@/features/projects/use-update-project'
import {
  doneVisibilityOptions,
  useDoneVisibilityOptionLabel,
} from '@/features/tenant-settings/done-visibility'
import { useClients } from '@/features/tenant-settings/use-clients'
import { useOrganization } from '@/features/tenant-settings/use-organization'

export const Route = createFileRoute('/projects/$projectId/settings')({
  component: ProjectSettingsRoute,
})

type SettingsTab = 'general' | 'labels' | 'statuses' | 'members'

const SETTINGS_PROJECTS_QUERY_KEY = ['projects', 'includeArchived'] as const

function useAllProjects() {
  return useQuery({
    queryKey: SETTINGS_PROJECTS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient<{ data: Project[] }>('/api/projects?includeArchived=true')
      return res.data
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

function ProjectSettingsRoute() {
  const navigate = useNavigate()
  const { projectId } = Route.useParams()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return <ProjectSettingsPage projectId={projectId} />
}

interface ProjectSettingsPageProps {
  projectId: string
}

function ProjectSettingsPage({ projectId }: ProjectSettingsPageProps) {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const projects = useAllProjects()
  // Members live on the board response (Phase 2a). For v1 we re-use that
  // payload rather than introducing a /api/projects/:id/members endpoint.
  // Note: this 404s for archived projects (the board route filters them);
  // we fall back to the projects list for the project name in that case.
  const board = useBoard(projectId)
  const [tab, setTab] = useState<SettingsTab>('general')

  // 401 from either query → cookies cleared by apiClient, bounce to login.
  useEffect(() => {
    const err = projects.error ?? board.error
    if (err instanceof ApiError && err.status === 401) {
      navigate({ to: '/login' })
    }
  }, [projects.error, board.error, navigate])

  // Project-admin is a project-level role off `project_users`, not a
  // tenant slug — see `project-admin.ts`. `board.data?.members` is
  // deliberately passed through undefined-able: an archived project 404s
  // the board, and unknown falls open so its owner can still unarchive.
  const canAdminister = useIsProjectAdmin(board.data?.members)

  const project = projects.data?.find((p) => p.id === projectId)
  const boardProject = board.data?.project as
    | { archivedAt?: string | null; name: string }
    | undefined
  const projectName = project?.name ?? boardProject?.name ?? 'Project'
  const isArchived = Boolean(project?.archivedAt) || Boolean(boardProject?.archivedAt)
  const isLoading = projects.isLoading || (board.isLoading && !isArchived)

  if (isLoading) {
    return (
      <output
        className="flex min-h-screen items-center justify-center text-sm text-fg-muted"
        aria-live="polite"
        aria-busy="true"
      >
        Loading project settings…
      </output>
    )
  }

  // Project not found in either source — usually a stale deep link.
  if (!project && !board.data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div
          role="alert"
          className="w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 dark:border-amber-700/60 dark:bg-amber-900/40"
        >
          <h1 className="mb-2 text-lg font-semibold text-amber-900 dark:text-amber-100">
            {t('projects.notFound.heading')}
          </h1>
          <p className="mb-4 text-sm text-amber-800 dark:text-amber-200">
            {t('projects.notFound.body')}
          </p>
          <Button variant="secondary" onClick={() => navigate({ to: '/' })}>
            {t('projects.settingsAccess.backToHome')}
          </Button>
        </div>
      </div>
    )
  }

  if (!canAdminister) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-fg">
            {t('projects.settingsAccess.heading')}
          </h1>
          <p className="mb-4 text-sm text-fg-muted">{t('projects.settingsAccess.body')}</p>
          {isArchived ? (
            <Button variant="secondary" onClick={() => navigate({ to: '/' })}>
              {t('projects.settingsAccess.backToHome')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate({ to: '/board/$projectId', params: { projectId } })}
            >
              {t('projects.settingsAccess.backToBoard')}
            </Button>
          )}
        </div>
      </div>
    )
  }

  // `statuses` off the cached board is deeply readonly; copy out so
  // sub-panels that expect a mutable array don't fight TypeScript.
  const statuses = board.data?.statuses ? [...board.data.statuses] : []

  return (
    <div className="flex min-h-screen flex-col bg-bg-muted">
      {/*
        Header content sits in the SAME `mx-auto max-w-6xl` column the
        tab row uses, so the title and the tabs share one left edge.
        Without it the header ran to the viewport's edge while the tabs
        were indented, which is what made the row look misplaced.
      */}
      <header className="border-b border-border bg-bg-elevated px-6 py-4">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {!isArchived ? (
              <Link
                to="/board/$projectId"
                params={{ projectId }}
                className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <title>Back</title>
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Back to board
              </Link>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <title>Back</title>
                  <path d="M15 18l-6-6 6-6" />
                </svg>
                Back to home
              </Link>
            )}
            <h1 className="truncate text-xl font-semibold text-fg">{projectName}</h1>
            <p className="text-sm text-fg-muted">
              Project settings
              {isArchived && (
                <span className="ms-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Archived
                </span>
              )}
            </p>
          </div>
        </div>
      </header>

      <TabBar label="Settings sections">
        <TabButton active={tab === 'general'} onClick={() => setTab('general')}>
          General
        </TabButton>
        <TabButton active={tab === 'labels'} onClick={() => setTab('labels')}>
          Labels
        </TabButton>
        <TabButton active={tab === 'statuses'} onClick={() => setTab('statuses')}>
          Statuses
        </TabButton>
        <TabButton active={tab === 'members'} onClick={() => setTab('members')}>
          Members
        </TabButton>
      </TabBar>

      <div className="flex flex-1 flex-col px-6 py-6">
        {tab === 'general' && project && <GeneralPanel project={project} isArchived={isArchived} />}
        {tab === 'labels' && <LabelsSettingsPanel projectId={projectId} />}
        {tab === 'statuses' && <StatusesSettingsPanel projectId={projectId} statuses={statuses} />}
        {tab === 'members' && (
          <MembersSettingsPanel projectId={projectId} members={board.data?.members} />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// General tab — project name / code / description summary + Danger zone.
// ---------------------------------------------------------------------------

interface GeneralPanelProps {
  project: Project
  isArchived: boolean
}

function GeneralPanel({ project, isArchived }: GeneralPanelProps) {
  const { t, i18n } = useTranslation('common')
  const [editOpen, setEditOpen] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()
  const archive = useArchiveProject()
  const unarchive = useUnarchiveProject()
  const clientsQuery = useClients()

  // Resolve the client display label from the tenant catalog. The
  // picker renders the same shape so the General-panel readout matches
  // exactly. Falls back to a muted "No client" when the project doesn't
  // have one assigned.
  const client = (clientsQuery.data ?? []).find((c) => c.id === project.clientId)
  const clientLabel = client
    ? client.isInternal
      ? `Internal (${client.companyName ?? client.displayName})`
      : client.companyName
        ? `${client.displayName} (${client.companyName})`
        : client.displayName
    : null

  const archivedDate = project.archivedAt
    ? new Date(project.archivedAt).toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const handleArchive = async () => {
    try {
      await archive.mutateAsync({ id: project.id })
      toast.success('Project archived')
      navigate({ to: '/' })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not archive project')
      setConfirmArchive(false)
    }
  }

  const handleUnarchive = async () => {
    try {
      await unarchive.mutateAsync({ id: project.id })
      toast.success('Project restored')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not unarchive project')
    }
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <div className="rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-fg">Project details</h2>
            <p className="text-sm text-fg-muted">Name, description, and code prefix.</p>
          </div>
          <Button onClick={() => setEditOpen(true)}>Edit project</Button>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">Name</dt>
            <dd className="mt-1 truncate text-sm text-fg">{project.name}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">Code</dt>
            <dd className="mt-1 truncate font-mono text-sm text-fg">{project.code ?? ''}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">Client</dt>
            <dd className="mt-1 truncate text-sm text-fg">
              {clientLabel ?? <span className="text-fg-subtle">No client</span>}
            </dd>
          </div>
          <div className="sm:col-span-3">
            <dt className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              Description
            </dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-fg">
              {project.description?.trim() || (
                <span className="text-fg-subtle">No description.</span>
              )}
            </dd>
          </div>
        </dl>
      </div>

      <DoneVisibilityCard project={project} />

      {/* Semantic tokens only. This panel was hardcoded to the light-theme
          red palette (`border-red-200 bg-red-50/40 text-red-900`), which is
          why it read wrong on Obsidian and Midnight — a pale wash and
          near-black text over a dark page. The tint and border derive from
          the per-theme `danger` token so all three themes get a red that
          belongs to that theme; the body copy is `fg-muted` rather than red
          because small red text over a red wash is the least legible part
          of the old design. */}
      <div className="rounded-lg border border-danger/40 bg-danger/5 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-danger">{t('projects.dangerZone.heading')}</h2>
        {isArchived ? (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-fg-muted">
              {archivedDate
                ? t('projects.dangerZone.archivedOn', { date: archivedDate })
                : t('projects.dangerZone.archivedNoDate')}
            </p>
            <Button
              variant="secondary"
              onClick={() => void handleUnarchive()}
              disabled={unarchive.isPending}
              className="whitespace-nowrap"
            >
              {unarchive.isPending
                ? t('projects.dangerZone.unarchivePending')
                : t('projects.dangerZone.unarchiveAction')}
            </Button>
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-fg-muted">{t('projects.dangerZone.archiveDescription')}</p>
            <Button
              variant="danger"
              onClick={() => setConfirmArchive(true)}
              className="whitespace-nowrap"
            >
              {t('projects.dangerZone.archiveAction')}
            </Button>
          </div>
        )}
      </div>

      <ProjectEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        project={{
          id: project.id,
          name: project.name,
          description: project.description ?? '',
          code: project.code ?? '',
          clientId: project.clientId ?? null,
        }}
      />

      <Modal
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        ariaLabel="Confirm archive project"
      >
        <Modal.Header>Archive this project?</Modal.Header>
        <Modal.Body>
          <p>
            Archived projects are hidden from the board list. You can unarchive them from the
            projects index.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="ghost"
            onClick={() => setConfirmArchive(false)}
            disabled={archive.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleArchive()}
            disabled={archive.isPending}
            variant="danger"
          >
            {archive.isPending ? 'Archiving…' : 'Archive project'}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Done-cards visibility card — per-project override of the org-wide window
// for hiding aged Done cards from board reads.
// ---------------------------------------------------------------------------

/** Sentinel select value for "inherit the organization default". */
const INHERIT_ORG_DEFAULT = 'inherit'

/**
 * Save-on-change select (no inline Save button — the settings tabs are
 * `useState`, so switching tabs unmounts this panel and a pending Save
 * would be a data-loss trap; the profile pickers set the precedent).
 * Commits via `useUpdateProject` and reverts the select on error.
 *
 * Rendered ungated like its sibling cards (Edit / Archive show for
 * everyone; the BE 403s non-admins) — conscious consistency choice.
 */
function DoneVisibilityCard({ project }: { project: Project }) {
  const { t } = useTranslation('common')
  const toast = useToast()
  const update = useUpdateProject()
  // GET /api/organization is ungated for members — safe to call here so
  // the inherit option can render the resolved org default.
  const org = useOrganization()
  const optionLabel = useDoneVisibilityOptionLabel()

  // Mirrors the org card's unknown-field guard: only write the field
  // when the API actually surfaced it. Against a BE that predates the
  // column the key is absent, and saving would toast success having
  // changed nothing.
  const fieldSupported = project.doneVisibleDays !== undefined

  const valueFromProject =
    project.doneVisibleDays == null ? INHERIT_ORG_DEFAULT : String(project.doneVisibleDays)
  const [value, setValue] = useState<string>(valueFromProject)

  // Re-hydrate when the projects list refetches (post-save server
  // normalization) or the user navigates to another project's settings.
  useEffect(() => {
    setValue(valueFromProject)
  }, [valueFromProject])

  // Pre-resolve the org-default label in code — no nested ICU. When the
  // org GET hasn't surfaced the field yet (older BE, transient error),
  // degrade to the label without the parenthetical value.
  const orgDefault = org.data?.doneVisibleDays
  const inheritLabel =
    orgDefault !== undefined
      ? t('projects.doneVisibility.optionInherit', { value: optionLabel(orgDefault) })
      : t('projects.doneVisibility.optionInheritUnknown')

  const handleChange = async (next: string) => {
    if (next === value || !fieldSupported) return
    const prev = value
    setValue(next)
    try {
      await update.mutateAsync({
        id: project.id,
        patch: { doneVisibleDays: next === INHERIT_ORG_DEFAULT ? null : Number(next) },
      })
      toast.success(t('projects.doneVisibility.toastUpdated'))
    } catch {
      setValue(prev)
      toast.error(t('projects.doneVisibility.toastError'))
    }
  }

  return (
    <div className="rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-fg">{t('projects.doneVisibility.title')}</h2>
      <p className="text-sm text-fg-muted">{t('projects.doneVisibility.subtitle')}</p>
      <div className="mt-4 flex flex-col gap-1">
        <label htmlFor="project-done-visibility" className="text-xs font-medium text-fg-muted">
          {t('projects.doneVisibility.label')}
        </label>
        <select
          id="project-done-visibility"
          aria-describedby="project-done-visibility-helper"
          className="max-w-sm rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-accent disabled:cursor-not-allowed disabled:opacity-60"
          value={value}
          disabled={update.isPending || !fieldSupported}
          onChange={(e) => void handleChange(e.target.value)}
        >
          <option value={INHERIT_ORG_DEFAULT}>{inheritLabel}</option>
          {doneVisibilityOptions(project.doneVisibleDays).map((days) => (
            <option key={days} value={String(days)}>
              {optionLabel(days)}
            </option>
          ))}
        </select>
        <p id="project-done-visibility-helper" className="mt-1 text-xs text-fg-muted">
          {t('projects.doneVisibility.helper')}
        </p>
      </div>
    </div>
  )
}
